import {  useEffect, useRef, useState } from "react";

import {
  getGeminiCompletion,
  getGeminiEmbedding,
} from "../ai/GeminiUtils";
import { logAnalyticsEvent } from "../engagement/Amplitude";
import { ChatSession, GenerativeModel } from "@google/generative-ai";
import { useQuery, useQueryClient } from "react-query";
import {
  useShowTempErrorMessage,
  useShowTempMessage,
} from "../shared/hooks/useUtilityContext";
import { useAuth } from "../authentication/AuthContext";
import ChatHeader from "../shared/ChatHeader";
import * as cheerio from "cheerio";
import {
  extractAllContent,
  extractArticle,
  extractDescription,
  extractImage,
  extractTitle,
} from "../shared/utils/ExtractionUtils";
import { TaskLink } from "./models/TaskLink";
import { cleanUpWhitespace } from "../shared/utils/StringUtils";
import { supabase } from "../../supabaseClient";
import LinkTile from "./components/LinkTile";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import MessageBubble from "../chat/components/MessageBubble";
import { AIModel, Message } from "./models/Message";

export default function GuideChat() {
 
  const [chat, setChat] = useState<ChatSession | null>(null);
  const queryClient = useQueryClient();
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [generatingTitle, setGeneratingTitle] = useState(false);
  const showTempErrorMessage = useShowTempErrorMessage();
  const showTempMessage = useShowTempMessage();
  const [suggestedQuestions, setSuggestedQuestions] = useState<
    string[] | null
  >();
  const [message, setMessage] = useState<string>("");
  const [lastMessage, setLastMessage] = useState<string>("");
  const [messages, setMessages] = useState<Message[] | null>(
    localStorage.getItem("chat") && localStorage.getItem("chat") !== undefined
      ? JSON.parse(localStorage.getItem("chat")!)
      : []
  );

  const { user } = useAuth();
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const messagesStartRef = useRef<null | HTMLDivElement>(null);
  const [showingLinkModal, setShowingLinkModal] = useState(false);
  const [addingUrl, setAddingUrl] = useState(false);
  const [url, setUrl] = useState("");
  const [stagedLink, setStagedLink] = useState<TaskLink | null>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [seedId, setSeedId] = useState<number | null>(
    localStorage.getItem("seedId")
      ? parseInt(localStorage.getItem("seedId")!)
      : null
  );
  const [summary, setSummary] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState(new Date().toISOString());
  const [summarizing, setSummarizing] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToTop = () => {
    messagesStartRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (seedId !== null) {
      localStorage.setItem("seedId", seedId.toString());
    } else {
      localStorage.removeItem("seedId");
    }
  }, [seedId]);

  useEffect(() => {
    const seedId = searchParams.get("seedId");

    if (seedId) {
      setSeedId(parseInt(seedId));
      setWaitingForResponse(true);
      localStorage.removeItem("chat");
      supabase
        .from("messages")
        .select("*")
        .or(`seed_id.eq.${seedId},id.eq.${seedId}`)
        .order("created_at", { ascending: true })
        .then(({ data, error }) => {
          if (error) {
            setWaitingForResponse(false);
            showTempErrorMessage(
              "Error fetching chat. Please try again later."
            );
          } else {
            setWaitingForResponse(false);
            setMessages(data);

            const seedMessage = data.find((m) => m.seed);
            setSummary(seedMessage?.description);
          }
        });
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("chat", JSON.stringify(messages));
    scrollToBottom();
  }, [messages]);

  const handleInput = () => {
    const textarea = messageRef.current;

    if (!textarea) return;
    const maxHeight = 300; // Set your desired max height here

    textarea.style.height = "auto";
    const newHeight = textarea.scrollHeight;
    if (newHeight > maxHeight) {
      textarea.style.height = maxHeight + "px";
      textarea.style.overflowY = "auto"; // Enable scrolling
    } else {
      textarea.style.height = newHeight + "px";
    }
  };

  useEffect(() => {
    handleInput();
  }, [message]);

  const sendMessage = async (message: string) => {
    logAnalyticsEvent("Send Guide Message");
    setWaitingForResponse(true);
   
  };

  const handleAddLink = async (url: string) => {
    setAddingUrl(true);
    logAnalyticsEvent("Add Link to Chat");

    if (url) {
      try {
        const result = await extractAllContent(url);
        const $ = cheerio.load(result);
        const title = extractTitle($);
        const description = extractDescription($);
        const image = extractImage($);
        const article = extractArticle($);

        const linkDetail: TaskLink = {
          url,
          title,
          description,
          image,
          contents: cleanUpWhitespace(article),
          user_id: user!.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        const suggestions = await getGeminiCompletion({
          prompt: `Give me a bulleted list of four suggested questions for this link.: ${cleanUpWhitespace(
            article
          )}\n\nSeparate each question with a new line.`,
        });

        console.log("suggestions", suggestions.split("\n"));
        setSuggestedQuestions(
          suggestions.split("\n").map((s) => s.trim().replace("- ", ""))
        );

        setStagedLink(linkDetail);
        setAddingUrl(false);

        handleInput();
        setUrl(""); // Clear the input field after submission
        setShowingLinkModal(false);

        setTimeout(() => {
          scrollToBottom();
        }, 200);
      } catch (error) {
        console.error("Error extracting link:", error);
        showTempErrorMessage("This link could not be extracted.");
        setAddingUrl(false);
      }
    }
  };

  async function handleSummary() {
    if (seedId !== null) {
      setSummarizing(true);
      logAnalyticsEvent("Update Summary", {
        rockId: seedId,
      });

      const update = (messages ?? []).length + 2 >= 10;

      // After the first summary,don't change title/slug
      if (update) {
        const contentToSummarize =
          messages
            ?.slice(messages.length - 6)
            .map((m) => m.message)
            .join("\n\n") ?? "";

        const addSummary = await getGeminiCompletion({
          prompt: `Summarize this text into bullet points:
    \n\n${contentToSummarize}`,

          model: "gemini-1.5-pro-latest",
        });

        await supabase
          .from("messages")
          .update({
            updated_at: new Date().toISOString(),
            updated_by: user!.id,
            description: summary + "\n\n" + addSummary,
          })
          .eq("id", seedId);
      } else {
        const contentToSummarize =
          messages?.map((m) => m.message).join("\n\n") ?? "";

        const title = await getGeminiCompletion({
          prompt: `Give me a concise title for this text:
    \n\n${contentToSummarize}`,
        });
        const newSummary = await getGeminiCompletion({
          prompt: `Summarize this text into bullet points:
    \n\n${contentToSummarize}`,
        });
        const tags = await getGeminiCompletion({
          prompt: `Give me a comma separated list of tags for this text:
    \n\n${contentToSummarize}`,
        });

        console.log("Title: ", title);
        console.log("Tags: ", tags);
        const embedding = await getGeminiEmbedding(title);
        setSummary(newSummary);

        await supabase
          .from("messages")
          .update({
            title,
            tags: tags
              .split(",")
              .map((t) => t.trim().replace(" ", "-").toLowerCase()),
            embedding,
            updated_at: new Date().toISOString(),
            updated_by: user!.id,
            description: newSummary,
          })
          .eq("id", seedId);
      }

      const links =
        messages
          ?.filter((m) => m.link !== null && m.link !== undefined)
          .map((m) => m.link!) ?? [];
      if (links.length > 0) {
        const promises = links?.map(async (link) => {
          console.log("link: ", link);
          const { data: linkData, error } = await supabase
            .from("links")
            .upsert(
              {
                created_at: new Date().toISOString(),
                title: link.title,
                description: link.description,
                image: link.image,
                url: link.url,
                user_id: user!.id,
                task_id: seedId,
                updated_at: new Date().toISOString(),
              },
              {
                onConflict: "url",
              }
            )
            .select("id")
            .single();
        });
        await Promise.all(promises ?? []);
      }
      setUpdatedAt(new Date().toISOString());
      setSummarizing(false);
    }
  }

  return (
    <div className="flex flex-col min-h-screen relative w-full">
      <div className="sticky top-0 z-20">
        <ChatHeader
          onRefresh={() => {
            logAnalyticsEvent("Refresh Chat");
            setMessages([]);
            setSeedId(null);
            setChat(null);
            navigate("/chat");
          }}
        />
      </div>
      <div className="flex flex-col grow items-center">
        <div className="grow  w-full sm:w-4/5 lg:w-1/2 max-w-3xl">
          <div ref={messagesStartRef} />
          {(messages === null || messages?.length === 0) && (
            <div className="text-3xl p-4 text-center">
              What are you building today?
            </div>
          )}
          {messages &&
            messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                msg={msg}
                messages={messages}
                setMessages={setMessages}
              />
            ))}

          {waitingForResponse && (
            <div className="w-full flex justify-center">
              <div className="loading loading-dots w-8"></div>
            </div>
          )}

          {suggestedQuestions && suggestedQuestions.length > 0 && (
            <div className="flex flex-col gap-2 my-4 mx-2">
              <div className="text-lg font-bold">Suggested Questions</div>
              {suggestedQuestions.map((question) => (
                <div
                  className="bg-neutral text-neutral-content shadow-md rounded-lg p-4 hover:bg-accent/80 hover:text-accent-content hover:shadow-lg cursor-pointer"
                  onClick={async () => {
                    logAnalyticsEvent("Select Suggested Question", {
                      question,
                    });
                    setSuggestedQuestions(null);
                    setLastMessage(message);
                    setMessage("");
                    setStagedLink(null);
                    await sendMessage(question);
                  }}
                >
                  {question}
                </div>
              ))}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {((messages ?? [])?.length === 0 ||
          user?.id === messages![0].user_id) && (
          <div
            className="sticky bottom-0 bg-neutral text-neutral-content shadow-lg rounded-t-lg p-4 w-full sm:w-4/5 lg:w-1/2 
            max-w-3xl flex flex-col"
          >
            <textarea
              ref={messageRef}
              rows={1}
              title="Message Input"
              placeholder="Type here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.shiftKey) {
                  e.preventDefault();
                  setSuggestedQuestions(null);
                  setLastMessage(message);
                  setMessage("");
                  setStagedLink(null);
                  sendMessage(message);
                }
              }}
              onPaste={(event) => {
                const pastedText = event.clipboardData.getData("text");
                const urlRegex =
                  /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/;

                if (urlRegex.test(pastedText)) {
                  console.log("Pasted text is a URL:", pastedText);
                  handleAddLink(pastedText);
                } else {
                  console.log("Pasted text is not a URL:", pastedText);
                  // Perform any desired actions with the pasted non-URL text
                }
              }}
              className="textarea w-full bg-neutral text-neutral-content overflow-auto"
            ></textarea>
            {stagedLink && stagedLink !== null && (
              <div className="shadow-sm shadow-accent rounded-md my-2">
                <LinkTile
                  link={stagedLink}
                  onDelete={() => {
                    setSuggestedQuestions(null);
                    setStagedLink(null);
                  }}
                />
              </div>
            )}
            <div className="flex justify-between items-center">
              {/* <ModelDropdown
                options={["gemini-pro", "gemini-1.5-pro-latest"]}
                onOptionSelected={handleOptionSelected}
              /> */}
              {summarizing && (
                <div className="flex items-center gap-2">
                  <div className="loading loading-sm loading-dots"></div>
                  <div className="text-sm">Summarizing...</div>
                </div>
              )}
              {addingUrl && (
                <div className="flex items-center gap-2">
                  <div className="loading loading-sm loading-dots"></div>
                  <div className="text-sm">Extracting...</div>
                </div>
              )}
              <div className="grow"></div>
              <div className="flex justify-end gap-2">
                {/* <button
                  className={`btn  text-neutral-content btn-sm mt-2 ${
                    stagedLink && stagedLink !== null
                      ? "text-neutral-content/50 btn-disabled"
                      : "btn-outline"
                  }`}
                  onClick={() => {
                    setShowingLinkModal(true);
                  }}
                  disabled={stagedLink !== undefined && stagedLink !== null}
                >
                  {stagedLink && stagedLink !== null
                    ? "Link Added"
                    : "Add Link"}
                </button> */}
                <button
                  className="btn btn-primary btn-sm mt-2"
                  onClick={async () => {
                    setSuggestedQuestions(null);
                    setLastMessage(message);
                    setMessage("");
                    setStagedLink(null);
                    await sendMessage(message);
                    setStagedLink(null);
                  }}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* <input
        type="checkbox"
        id="my_modal_6"
        className="modal-toggle"
        checked={showingLinkModal}
      /> */}
      {/* <div
        className="modal"
        role="dialog"
      >
        <div className="modal-box">
          <h3 className="font-bold text-lg">Add a Link</h3>
          <p className="py-4">
            We will extract the contents from the link and add it to the chat
          </p>
          <form onSubmit={handleAddLink}>
            <input
              type="text"
              className="input input-bordered w-full"
              placeholder="Enter URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <div className="modal-action">
              {addingUrl && <div className="loading loading-lg"></div>}
              <button
                type="submit"
                className="btn btn-primary"
              >
                Submit
              </button>
              <label
                htmlFor="my_modal_6"
                className="btn"
                onClick={() => {
                  setShowingLinkModal(false);
                }}
              >
                Close
              </label>
            </div>
          </form>
        </div>
      </div> */}
    </div>
  );
}
